import { ButtonsGroup, useModal, withModalConditionalRender } from '@billy/design-system'
import { ComponentType, ReactElement, useCallback, useMemo } from 'react'

import { BlockCancelInvoiceActionModals } from '@features/invoices/elements/BlockCancelInvoiceActionModals'
import { CancelInvoiceModal } from '@features/invoices/elements/CancelInvoiceModal'
import { SendAsEmailModal as SendAsEmailModalComponent } from '@features/invoices/elements/SendAsEmailModal'
import { SendAsEmailModalVariant } from '@features/invoices/enums/sendAsEmailModalVariant'
import { useSendAsEmail } from '@features/invoices/hooks/sendAsEmail/useSendAsEmail'
import { SendAsEmailForm } from '@features/invoices/types/sendAsEmailForm'
import { getCancelDepositInvoiceEligibility } from '@features/invoices/utils/depositInvoices/getCancelDepositInvoiceEligibility'
import { getInvoiceQueryKey } from '@features/invoices/utils/getInvoiceQueryKey'
import { isDocumentTypeCreditNote } from '@features/invoices/utils/isDocumentTypeCreditNote'

import { queryClient } from '../../../../../../config/queryClient'
import { ModalId } from '../../../../../../enums/modalId'
import { useInvoice } from '../../contexts/invoiceContext'
import { CancelInvoiceButton } from './elements/CancelInvoiceButton'
import { CreateCreditNoteButton } from './elements/CreateCreditNoteButton'
import { DuplicateInvoiceButton } from './elements/DuplicateInvoiceButton'
import { ExternalViewInvoiceButton } from './elements/ExternalViewInvoiceButton'
import { InvoiceActionsMenu } from './elements/InvoiceActionsMenu'
import { MarkInvoiceAsSentButton } from './elements/MarkInvoiceAsSentButton'
import { RegisterPaymentButton } from './elements/RegisterPaymentButton'
import { SendPaymentReminderButton } from './elements/SendPaymentReminderButton'
import { ShareInvoiceMenu } from './elements/ShareInvoiceMenu'
import { UnmarkInvoiceAsSentButton } from './elements/UnmarkInvoiceAsSentButton'
import { InvoiceActionType } from './enums/InvoiceActionType'
import { useInvoiceViewActions } from './hooks/useInvoiceViewActions'

const SendAsEmailModal = withModalConditionalRender(SendAsEmailModalComponent)

const invoiceActionTypeToComponent: Record<InvoiceActionType, ComponentType> = {
  [InvoiceActionType.RegisterPayment]: RegisterPaymentButton,
  [InvoiceActionType.Share]: ShareInvoiceMenu,
  [InvoiceActionType.OpenInZervant]: ExternalViewInvoiceButton,
  [InvoiceActionType.SendPaymentReminder]: SendPaymentReminderButton,
  [InvoiceActionType.Duplicate]: DuplicateInvoiceButton,
  [InvoiceActionType.Cancel]: CancelInvoiceButton,
  [InvoiceActionType.CreateCreditNote]: CreateCreditNoteButton,
  [InvoiceActionType.MarkAsSent]: MarkInvoiceAsSentButton,
  [InvoiceActionType.UnmarkAsSent]: UnmarkInvoiceAsSentButton,
}

const renderInvoiceActionComponent = (actionType: InvoiceActionType): ReactElement => {
  const ActionComponent = invoiceActionTypeToComponent[actionType]
  return <ActionComponent key={actionType} />
}

export const InvoiceViewActions = () => {
  const { billyApiExternalInvoiceLoading, documentType, invoice, invoiceId, isLoading: isInvoiceLoading } = useInvoice()
  const { invoiceActionsDisplayedAsButtons } = useInvoiceViewActions()
  const { close: closeSendPaymentReminderModal } = useModal(ModalId.SendPaymentReminderModal)
  const { sendAsEmail: sendPaymentReminderAsEmail, isProcessing: isSubmitting } = useSendAsEmail(
    SendAsEmailModalVariant.Reminder,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(getInvoiceQueryKey(invoiceId, documentType))
        closeSendPaymentReminderModal()
      },
    },
  )

  const invoiceNumber = useMemo(
    () => (isDocumentTypeCreditNote(documentType) ? invoice?.creditNoteNumber : invoice?.invoiceNumber),
    [documentType, invoice?.creditNoteNumber, invoice?.invoiceNumber],
  )

  const cancelDepositInvoiceEligibility = getCancelDepositInvoiceEligibility(invoice)
  const isInvoiceViewActionsLoading = isInvoiceLoading || billyApiExternalInvoiceLoading

  const onSendReminderSubmit = useCallback(
    (formData: SendAsEmailForm) => {
      sendPaymentReminderAsEmail({
        documentType,
        formData,
        invoiceId: Number(invoiceId),
      })
    },
    [documentType, invoiceId, sendPaymentReminderAsEmail],
  )

  return (
    <>
      {!isInvoiceViewActionsLoading && (
        <ButtonsGroup>
          {invoiceActionsDisplayedAsButtons.map(renderInvoiceActionComponent)}
          <InvoiceActionsMenu />
        </ButtonsGroup>
      )}
      <CancelInvoiceModal documentType={documentType} id={ModalId.InvoiceCancellationModal} invoiceId={invoiceId} />
      <BlockCancelInvoiceActionModals
        cancelDepositInvoiceEligibility={cancelDepositInvoiceEligibility}
        invoiceId={invoiceId}
      />
      <SendAsEmailModal
        contactId={String(invoice?.customer.externalContactId)}
        documentLocale={invoice?.locale}
        documentType={documentType}
        id={ModalId.SendPaymentReminderModal}
        isSubmitting={isSubmitting}
        invoiceNumber={invoiceNumber || ''}
        onSubmit={onSendReminderSubmit}
        variant={SendAsEmailModalVariant.Reminder}
        view="invoiceView"
      />
    </>
  )
}
